import React, { useEffect, useRef, useState } from "react";
import { useRecordContext } from "react-admin";
import { authClient } from "../../server";

type DeviceState = {
  devices: { hash: string; label: string; lastUsedAt: string | null }[];
  nextRemovalAt: string | null;
};

/** Support edits use the locked, audited endpoint, never a stale user-form snapshot. */
export function UserPlusDevices() {
  const record = useRecordContext();
  const [loaded, setLoaded] = useState<{ path: string; state: DeviceState }>();
  const [hash, setHash] = useState("");
  const [label, setLabel] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const path = `/admin/users/${record.id}/plus-devices`;
  const generation = useRef(0);
  const state = loaded?.path === path ? loaded.state : undefined;
  /** Let requests finish, but invalidate every response when the edited user
   * changes so another account's devices can never enable these controls. */
  useEffect(() => {
    const request = ++generation.current;
    setLoaded(undefined);
    setHash("");
    setLabel("");
    setError("");
    setBusy(false);
    void authClient.get(path).then(
      (response) => {
        if (request === generation.current)
          setLoaded({ path, state: response.json });
      },
      () => {
        if (request === generation.current) setError("Unable to load devices.");
      },
    );
    return () => {
      generation.current++;
    };
  }, [path]);
  const change = async (
    action: "add" | "remove" | "reset",
    deviceHash?: string,
  ) => {
    if (!state || busy) return;
    if (
      !window.confirm(
        action === "reset"
          ? "Reset all devices and the removal cooldown?"
          : `${action === "add" ? "Add" : "Remove"} this device? This support action clears the cooldown.`,
      )
    )
      return;
    setBusy(true);
    setError("");
    const request = generation.current;
    try {
      const response = await authClient.post(path, {
        action,
        hash: deviceHash,
        label,
      });
      if (request !== generation.current) return;
      setLoaded({ path, state: response.json });
      setHash("");
      setLabel("");
    } catch (error: any) {
      if (request === generation.current)
        setError(error.body?.message || "Unable to change devices.");
    } finally {
      if (request === generation.current) setBusy(false);
    }
  };
  return (
    <div style={{ marginTop: 20 }}>
      <p>
        Plus permits three devices. Support changes bypass and clear the weekly
        removal cooldown.
      </p>
      {error && <p role="alert">{error}</p>}
      {state?.nextRemovalAt && (
        <p>
          Next normal removal: {new Date(state.nextRemovalAt).toLocaleString()}
        </p>
      )}
      {state?.devices.map((device) => (
        <div key={device.hash} style={{ marginBottom: 16 }}>
          <strong>{device.label}</strong>
          <p>{device.hash}</p>
          <p>
            Last used:{" "}
            {device.lastUsedAt
              ? new Date(device.lastUsedAt).toLocaleString()
              : "Not yet used"}
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => void change("remove", device.hash)}
          >
            Remove
          </button>
        </div>
      ))}
      <p>
        <label>
          Full device code{" "}
          <input
            value={hash}
            onChange={(e) => setHash(e.target.value)}
            maxLength={43}
          />
        </label>
      </p>
      <p>
        <label>
          Description{" "}
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={100}
          />
        </label>
      </p>
      <button
        type="button"
        disabled={
          busy ||
          !/^[A-Za-z0-9_-]{43}$/.test(hash) ||
          !state ||
          state.devices.length >= 3
        }
        onClick={() => void change("add", hash)}
      >
        Add device
      </button>{" "}
      <button
        type="button"
        disabled={busy || !state}
        onClick={() => void change("reset")}
      >
        Reset devices and cooldown
      </button>
    </div>
  );
}
